import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SignaturePad from "@/components/SignaturePad";
import { toast } from "sonner";
import { formatBRL, STATUS_STYLES, DOC_TYPES, DOC_STATUS, BUDGET_STATUS, SERVICE_STATUS } from "@/lib/constants";
import { ArrowRightLeft } from "lucide-react";
import { PostServiceDialog } from "@/components/PostServiceDialog";
import { ArrowLeft, Download, MessageCircle, Eye, PenLine, Send, Copy, Pencil, Files, CheckCircle2, Loader2, ShieldCheck } from "lucide-react";

export default function DocumentView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [postOpen, setPostOpen] = useState(false);
  const [client, setClient] = useState(null);
  const [company, setCompany] = useState(null);
  const [signOpen, setSignOpen] = useState(false);
  const [sig, setSig] = useState(null);
  const [signName, setSignName] = useState("");
  const [busy, setBusy] = useState(false);

  const publicUrl = doc ? `${window.location.origin}/doc/${doc.share_token}` : "";
  const pdfUrl = doc ? `${API}/documents/${doc.id}/pdf` : "";

  const load = () => api.get(`/documents/${id}`).then(async ({ data }) => {
    setDoc(data);
    setSignName((data.signature?.name) || "");
    const [cl, comp] = await Promise.all([api.get(`/clients/${data.client_id}`).catch(() => null), api.get("/company")]);
    if (cl) setClient(cl.data.client);
    setCompany(comp.data);
  }).catch(() => { toast.error("Documento não encontrado"); navigate("/documents"); });
  useEffect(() => { load(); }, [id]);

  if (!doc) return <div className="h-64 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  const wa = client?.whatsapp?.replace(/\D/g, "");
  const waMsg = encodeURIComponent(`Olá ${client?.name || ""}! Segue seu ${DOC_TYPES[doc.type]?.label} ${doc.number} no valor de ${formatBRL(doc.total)}.\nVisualize e assine aqui: ${publicUrl}`);

  const setStatus = async (status) => { await api.post(`/documents/${doc.id}/status`, { status }); toast.success("Status atualizado"); load(); if (["Finalizada", "Concluído"].includes(status)) setPostOpen(true); };

  const openPdf = async () => {
    setBusy(true);
    try {
      const res = await fetch(pdfUrl, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }, credentials: "include" });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch { toast.error("Erro ao gerar PDF"); } finally { setBusy(false); }
  };

  const sendForSign = async () => {
    await api.post(`/documents/${doc.id}/status`, { status: "Enviado para assinatura" });
    if (wa) window.open(`https://wa.me/${wa}?text=${waMsg}`, "_blank");
    else { navigator.clipboard.writeText(publicUrl); toast.success("Link copiado! Envie ao cliente."); }
    load();
  };

  const confirmSign = async () => {
    if (!sig) { toast.error("Assine no campo"); return; }
    if (!signName) { toast.error("Informe o nome do cliente"); return; }
    setBusy(true);
    try {
      await api.post(`/documents/${doc.id}/sign`, { name: signName, signature: sig });
      toast.success("Documento assinado com sucesso!");
      setSignOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); } finally { setBusy(false); }
  };

  const duplicate = async () => { const { data } = await api.post(`/documents/${doc.id}/duplicate`); toast.success("Documento duplicado"); navigate(`/documents/${data.id}/edit`); };
  const signed = doc.status === "Assinado";

  return (
    <div className="space-y-4 max-w-3xl">
      <button onClick={() => navigate("/documents")} className="flex items-center gap-1 text-muted-foreground text-sm" data-testid="view-back"><ArrowLeft className="h-4 w-4" /> Voltar</button>

      {signed && (
        <Card className="p-4 bg-secondary/10 border-secondary/30 flex items-center gap-3">
          <CheckCircle2 className="h-8 w-8 text-secondary shrink-0" />
          <div><p className="font-display font-bold text-secondary">Documento assinado com sucesso.</p><p className="text-xs text-muted-foreground">Assinado por {doc.signature?.name} em {doc.signature?.signed_at} • ID {doc.signature?.audit_id}</p></div>
        </Card>
      )}

      <Card className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1"><span className="text-xs font-semibold px-2 py-0.5 rounded-md bg-accent">{DOC_TYPES[doc.type]?.label}</span><span className="font-mono text-sm text-muted-foreground">{doc.number}</span></div>
            <h1 className="font-display font-extrabold text-2xl">{client?.name || "Cliente"}</h1>
            <p className="text-sm text-muted-foreground">{doc.date}</p>
          </div>
          <div className="text-right">
            <p className="font-display font-extrabold text-2xl text-secondary">{formatBRL(doc.total)}</p>
            <Badge variant="outline" className={`${STATUS_STYLES[doc.status] || ""}`}>{doc.status}</Badge>
          </div>
        </div>

        <div className="mt-4">
          <Label className="text-xs">Alterar status</Label>
          <Select value={doc.status} onValueChange={setStatus} disabled={signed}>
            <SelectTrigger className="mt-1 h-10" data-testid="view-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>{(doc.type === "budget" ? BUDGET_STATUS : ["nota_servico", "service_order"].includes(doc.type) ? [...SERVICE_STATUS, "Enviado para assinatura", "Assinado", "Concluído"] : DOC_STATUS).filter((s, i, a) => a.indexOf(s) === i).concat(DOC_STATUS.includes(doc.status) ? [] : [doc.status]).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          {doc.type === "budget" && ["Aprovado", "Assinado"].includes(doc.status) && !doc.converted_to && (
            <Button className="w-full h-12 mt-3" onClick={async () => { const { data } = await api.post(`/documents/${doc.id}/convert`); toast.success(`Nota de Serviço ${data.number} criada!`); navigate(`/documents/${data.id}`); }} data-testid="convert-budget-btn"><ArrowRightLeft className="h-4 w-4 mr-1" /> Converter em Nota de Serviço</Button>
          )}
          {doc.converted_to && <Button variant="outline" className="w-full h-11 mt-3" onClick={() => navigate(`/documents/${doc.converted_to}`)} data-testid="open-converted-btn">Abrir Nota de Serviço gerada</Button>}
          {["nota_servico", "service_order"].includes(doc.type) && !["Finalizada", "Concluído", "Cancelado"].includes(doc.status) && (
            <Button className="w-full h-12 mt-3 bg-secondary hover:bg-secondary/90" onClick={() => setStatus("Finalizada")} data-testid="finish-service-btn"><CheckCircle2 className="h-4 w-4 mr-1" /> Finalizar serviço</Button>
          )}
          {["nota_servico", "service_order"].includes(doc.type) && ["Finalizada", "Concluído"].includes(doc.status) && (
            <Button variant="outline" className="w-full h-11 mt-3" onClick={() => setPostOpen(true)} data-testid="post-service-btn"><CheckCircle2 className="h-4 w-4 mr-1 text-secondary" /> Pós-venda: enviar, avaliar, agendar</Button>
          )}
          <PostServiceDialog open={postOpen} onOpenChange={setPostOpen} doc={doc} client={client} company={company} />
        </div>
      </Card>

      {/* Compartilhamento */}
      <Card className="p-5 space-y-3">
        <h2 className="font-display font-bold text-lg">Documento pronto! Compartilhe 🎉</h2>
        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" className="h-12" onClick={openPdf} disabled={busy} data-testid="view-pdf-btn"><Eye className="h-4 w-4 mr-1" /> Visualizar PDF</Button>
          <a href={pdfUrl} target="_blank" rel="noreferrer"><Button variant="outline" className="w-full h-12" data-testid="download-pdf-btn"><Download className="h-4 w-4 mr-1" /> Baixar PDF</Button></a>
          {wa ? (
            <a href={`https://wa.me/${wa}?text=${waMsg}`} target="_blank" rel="noreferrer"><Button className="w-full h-12 bg-[#25D366] hover:bg-[#1da851]" data-testid="whatsapp-btn"><MessageCircle className="h-4 w-4 mr-1" /> WhatsApp</Button></a>
          ) : (
            <Button className="h-12 bg-[#25D366] hover:bg-[#1da851]" onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("Link copiado!"); }} data-testid="whatsapp-btn"><MessageCircle className="h-4 w-4 mr-1" /> WhatsApp</Button>
          )}
          <Button variant="outline" className="h-12" onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("Link copiado!"); }} data-testid="copy-link-btn"><Copy className="h-4 w-4 mr-1" /> Copiar link</Button>
        </div>
      </Card>

      {/* Assinatura */}
      {!signed && (
        <Card className="p-5 space-y-3">
          <h2 className="font-display font-bold text-lg flex items-center gap-2"><PenLine className="h-5 w-5" /> Assinatura do Cliente</h2>
          <p className="text-sm text-muted-foreground">Colete as assinaturas do cliente e do técnico na hora da entrega, ou envie um link seguro.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Button className="h-12 sm:col-span-2" onClick={() => navigate(`/documents/${doc.id}/sign`)} data-testid="collect-signatures-btn"><PenLine className="h-4 w-4 mr-1" /> Coletar assinaturas (cliente + técnico)</Button>
            <Button variant="outline" className="h-12" onClick={() => setSignOpen(true)} data-testid="sign-now-btn"><PenLine className="h-4 w-4 mr-1" /> Só o cliente assina</Button>
            <Button variant="outline" className="h-12" onClick={sendForSign} data-testid="send-sign-btn"><Send className="h-4 w-4 mr-1" /> Enviar para o cliente assinar</Button>
          </div>
        </Card>
      )}

      {/* Ações */}
      <div className="flex gap-2">
        {!signed && <Button variant="outline" className="flex-1 h-11" onClick={() => navigate(`/documents/${doc.id}/edit`)} data-testid="edit-doc-btn"><Pencil className="h-4 w-4 mr-1" /> Editar</Button>}
        <Button variant="outline" className="flex-1 h-11" onClick={duplicate} data-testid="duplicate-doc-btn"><Files className="h-4 w-4 mr-1" /> Duplicar</Button>
      </div>

      <Dialog open={signOpen} onOpenChange={setSignOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Assinatura do Cliente</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome do cliente</Label><Input value={signName} onChange={(e) => setSignName(e.target.value)} className="mt-1 h-11" data-testid="sign-name-input" /></div>
            <SignaturePad onChange={setSig} />
            <p className="text-xs text-muted-foreground flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> A data, hora e um identificador único serão registrados.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSignOpen(false)} data-testid="sign-cancel-btn">Cancelar</Button>
            <Button onClick={confirmSign} disabled={busy} data-testid="sign-confirm-btn">{busy ? "Salvando..." : "Confirmar assinatura"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
